import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import IdCropSheet from "./IdCropSheet";

/**
 * iter205 — Vendor ID re-verification banner. Renders at the top of the
 * vendor dashboard when the async Florence-2 OCR check flagged the ID as
 * unreadable. Tapping "Upload New ID" opens the same IdCropSheet the
 * onboarding flow uses; on success we POST to `/vendor/me/reverify-id`
 * which resets the verification status and re-enqueues the background
 * check.
 *
 * The banner is a no-op unless `vendor.id_verification_status === "failed"`.
 * Locale strings match the two Habano languages we support (ES default).
 */
export default function IdVerificationBanner({ vendor, onDone }) {
  const [cropFile, setCropFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  if (!vendor || vendor.id_verification_status !== "failed") return null;

  const lang = (vendor.language || "es").toLowerCase();
  const isEn = lang === "en";
  const title = isEn
    ? "Your ID could not be verified"
    : "Tu identificación no pudo ser verificada";
  const body = isEn
    ? "Please upload a new one to keep your account active."
    : "Por favor sube una nueva para mantener tu cuenta activa.";
  const cta = isEn ? "Upload New ID" : "Subir Nueva ID";

  const openFilePicker = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,application/pdf";
    input.onchange = () => {
      if (input.files?.[0]) setCropFile(input.files[0]);
    };
    input.click();
  };

  return (
    <div
      data-testid="id-verify-banner"
      className="mb-6 flex flex-col gap-3 p-4"
      style={{
        background: "#171717",
        border: "1px solid #E8B84B",
        borderRadius: 8,
      }}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: "#E8B84B" }} strokeWidth={2} />
        <div className="flex-1 min-w-0">
          <div
            data-testid="id-verify-banner-title"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 22,
              color: "#F5F1E8",
              lineHeight: 1.2,
            }}
          >
            {title}
          </div>
          <div
            data-testid="id-verify-banner-body"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              color: "#C7C0B4",
              marginTop: 6,
              lineHeight: 1.4,
            }}
          >
            {body}
          </div>
        </div>
      </div>
      <button
        type="button"
        data-testid="id-verify-banner-cta"
        onClick={openFilePicker}
        disabled={uploading}
        className="w-full flex items-center justify-center rounded-lg transition-opacity disabled:opacity-60"
        style={{
          minHeight: 48,
          background: "linear-gradient(135deg, #F5CC55 0%, #D4A843 100%)",
          color: "#1a0f06",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 16,
          fontWeight: 700,
          border: "none",
          cursor: "pointer",
        }}
      >
        {uploading ? "…" : cta}
      </button>

      {cropFile && (
        <IdCropSheet
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onComplete={async ({ id_url }) => {
            setUploading(true);
            try {
              await api.post("/vendor/me/reverify-id", { government_id_url: id_url });
              toast.success(isEn ? "Uploaded — verifying" : "Subido — verificando");
              setCropFile(null);
              onDone?.();
            } catch (e) {
              toast.error(e.response?.data?.detail || (isEn ? "Upload failed" : "Error al subir"));
            } finally {
              setUploading(false);
            }
          }}
        />
      )}
    </div>
  );
}
